import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, Play, CheckCircle, Warning } from "@phosphor-icons/react";

// ClickUp tab "Write-Back": FR → ClickUp one-way push settings + log
export default function WritebackPanel() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [lists, setLists] = useState([]);
  const [mapping, setMapping] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/clickup/writeback/config").then(r => setCfg(r.data)).catch(() => {});
    api.get("/clickup/writeback/log?limit=40").then(r => setLog(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/clickup/watchlist").then(async r => {
      const ids = r.data.list_ids || [];
      const rows = await Promise.all(ids.map(async id => {
        try { const s = await api.get(`/clickup/writeback/list-statuses/${id}`); return { id, statuses: s.data.statuses, mapping: s.data.mapping }; }
        catch { return { id, statuses: [], mapping: {} }; }
      }));
      setLists(rows);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (cfg?.default_list_id) api.get(`/clickup/writeback/list-statuses/${cfg.default_list_id}`).then(r => setMapping(r.data)).catch(() => setMapping(null));
  }, [cfg?.default_list_id]);

  const save = async (patch) => {
    try { const r = await api.post("/clickup/writeback/config", patch); setCfg(r.data); toast.success("Saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save"); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const r = await api.post("/clickup/writeback/run-now"); toast.success(r.data.ok ? `Checked ${r.data.checked}, pushed ${r.data.pushed}` : r.data.reason); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Run failed"); }
    finally { setBusy(false); }
  };
  if (!cfg) return <div className="text-sm text-slate-500 font-bold">Loading…</div>;

  const Toggle = ({ k, label, hint }) => (
    <label className="flex items-start gap-3 p-3 border-2 border-slate-200 bg-white cursor-pointer">
      <input type="checkbox" checked={!!cfg[k]} onChange={e => save({ [k]: e.target.checked })} data-testid={`wb-${k}`} className="mt-1 h-4 w-4"/>
      <span><span className="block text-xs font-black uppercase tracking-widest">{label}</span>{hint && <span className="block text-[11px] text-slate-600 mt-0.5">{hint}</span>}</span>
    </label>
  );

  return (
    <div className="space-y-4" data-testid="clickup-writeback-panel">
      <div className={`p-4 border-2 ${cfg.enabled ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50"} flex flex-wrap items-center gap-3`}>
        <div className="flex-1 min-w-[240px]">
          <div className="font-black uppercase text-sm tracking-widest inline-flex items-center gap-2">Field Radar <ArrowRight weight="bold"/> ClickUp write-back {cfg.enabled ? <span className="text-emerald-700 text-[10px]">ON</span> : <span className="text-slate-500 text-[10px]">OFF</span>}</div>
          <p className="text-xs text-slate-600 mt-1">Every 2 minutes, any WO whose status changed (CSV import, Jobs Board, tech's phone, Radar chat, email sync) is pushed to its ClickUp task. One-way — nothing is read back unless you allow it below.</p>
          {cfg.last_run_at && <p className="text-[11px] text-slate-500 mt-1">Last run {new Date(cfg.last_run_at).toLocaleString()} · checked {cfg.last_run_checked} · pushed {cfg.last_run_pushed}</p>}
        </div>
        <button onClick={() => save({ enabled: !cfg.enabled })} data-testid="wb-enabled-toggle"
          className={`h-10 px-4 font-black uppercase text-xs tracking-widest ${cfg.enabled ? "bg-slate-900 text-white" : "bg-emerald-600 text-white"}`}>{cfg.enabled ? "Turn off" : "Turn on"}</button>
        <button onClick={runNow} disabled={busy || !cfg.enabled} data-testid="wb-run-now" className="h-10 px-3 border-2 border-slate-900 font-black uppercase text-xs tracking-widest inline-flex items-center gap-1 disabled:opacity-40"><Play weight="fill" size={12}/> Run now</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Toggle k="push_comment" label="Post a comment" hint='"Field Radar: Completed 9/11 by Jhan — replaced P-trap. Photos: link"'/>
        <Toggle k="attach_photos" label="Attach after-photos on completion" hint="Up to 6 photos uploaded to the task"/>
        <Toggle k="create_missing" label="Create a task when no match" hint="Only for WOs with no ClickUp task — goes to the default list below"/>
        <Toggle k="inbound_mirror_enabled" label="Allow ClickUp → Field Radar status mirror" hint="Turn OFF for strict one-way. Comments/files from ClickUp still show in the feed."/>
      </div>

      <div className="p-3 border-2 border-slate-200 bg-white">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Default list for new tasks (from your watchlist)</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {lists.length === 0 && <span className="text-xs text-slate-500">Add lists on the Team Feed watchlist first.</span>}
          {lists.map(l => (
            <button key={l.id} onClick={() => save({ default_list_id: l.id })} data-testid={`wb-list-${l.id}`}
              className={`h-9 px-3 border-2 text-xs font-black ${cfg.default_list_id === l.id ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}>List {l.id}</button>
          ))}
        </div>
        {mapping && (
          <div className="mt-3 text-[11px]" data-testid="wb-mapping">
            <div className="font-black uppercase tracking-widest text-slate-500 text-[10px]">Status mapping for this list</div>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              {Object.entries(mapping.mapping).filter(([fr]) => ["pool", "assigned", "in_progress", "follow_up", "on_hold", "completed", "canceled"].includes(fr)).map(([fr, cu]) => (
                <div key={fr} className="flex justify-between border-b border-slate-100 py-1"><span className="font-bold">{fr.replace("_", " ")}</span><span className={cu ? "text-emerald-700 font-black" : "text-rose-600 font-black"}>{cu || "no match — skipped"}</span></div>
              ))}
            </div>
            <div className="text-slate-500 mt-1">ClickUp statuses on this list: {mapping.statuses.join(" · ")}</div>
          </div>
        )}
      </div>

      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Push log</div>
        <div className="mt-1 border-2 border-slate-200 divide-y divide-slate-100 bg-white max-h-80 overflow-y-auto" data-testid="wb-log">
          {log.length === 0 && <div className="p-3 text-xs text-slate-500">Nothing pushed yet.</div>}
          {log.map((r, i) => (
            <div key={i} className="p-2 text-xs flex items-start gap-2">
              {r.ok ? <CheckCircle weight="fill" size={14} className="text-emerald-600 mt-0.5"/> : <Warning weight="fill" size={14} className={`${r.skipped ? "text-slate-400" : "text-rose-600"} mt-0.5`}/>}
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{r.wo ? `WO ${r.wo} · ` : ""}{r.title} → <span className="uppercase">{r.fr_status}</span>{r.cu_status ? ` (${r.cu_status})` : ""}</div>
                <div className="text-slate-600">{r.message}{r.task_url && <> · <a href={r.task_url} target="_blank" rel="noreferrer" className="underline">open task</a></>}</div>
              </div>
              <div className="text-slate-400 flex-shrink-0">{new Date(r.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
